import React, {
  useEffect, useState, useMemo, useCallback, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import StatusBar from '../../../components/status-bar/status-bar.component';
import Loader from '../../../components/loader/loader.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import TextBlock from '../../../components/text-block/text-block.component';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import ListItemImage from '../../../components/list-item-image/list-item-image.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import ContentHeader from '../../../components/content-header/content-header.component';
import ConfirmModal from '../../../components/confirm-modal/confirm-modal.component';
import ActionButton from '../../../components/action-button/action-button.component';
import Image from '../../../components/image/image.component';
import PullToRefresh from '../../../components/pull-to-refresh/pull-to-refresh.component';
import Form from '../../../components/form/form.component';
import FormError from '../../../components/form-error/form-error.component';
import FormContainer from '../../../components/form-container/form-container.component';
import TableCellExtended from '../../../components/table-cell-extended/table-cell-extended.component';
import ContentListContainerOffset
  from '../../app/common/content-list-container-offset/content-list-container-offset.component';
import Controls from '../../app/controls/controls.component';
import SettingsPageTabs from '../settings-page-tabs/settings-page-tabs.component';
import SettingsAccountModal from './settings-account-modal/settings-account-modal.component';
import SettingsAccountNotificationModal
  from './settings-account-notification-modal/settings-account-notification-modal.component';
import DeleteItemIcon from '../../../icons/delete-item-icon';
import MoreIcon from '../../../icons/more-icon';
import SendNotificationIcon from '../../../icons/send-notification-icon';
import SettingsAccountListValidator from './settings-account-list.validator';
import FormService from '../../../services/form-service';
import FormatService from '../../../services/format-service';
import useFormStructureModified from '../../../hooks/use-form-structure-modified';
import useServerFormErrors from '../../../hooks/use-server-form-errors';
import useScreenSmall from '../../../hooks/use-screen-small';
import {
  deleteAccount, getAccountListData, saveAccountListData, sendAccountNotification,
} from './settings-account-list.action';
import { getSettingsData } from '../settings.action';
import { resetFormErrors } from '../../app/app.action';
import { ACCOUNT_LIST_VIEW, ACCOUNT_CREATE, ACCOUNT_DELETE } from '../../../constants/entity/permissions';
import { TAB_NAME_ACCOUNT_ADMINISTRATION } from '../../../constants/system/tab-names';
import { FORM_ID_SETTINGS_ACCOUNT_LIST } from '../../../constants/system/form-ids';
import { DATA_ACCOUNT_ID } from '../../../constants/system/data-attributes';
import { QUERY_PARAM_TAB } from '../../../constants/system/query-params';
import ACCOUNT_SUBSCRIPTION_PLAN_DEFAULT from '../../../constants/entity/account-subscription-plans';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';

const SettingsAccountList: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement | null => {
  const HEADER_NAME_ACTIONS: string = 'actions';

  const {
    isDataLoaded,
    isRightDrawerOpen,
    currentUser,
    accountListData,
    roleList,
    accountPlainList,
    accountSubscriptionPlanList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
  } = formProps;

  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();
  const isSmallScreen: boolean = useScreenSmall();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;

  const [isAccountModalOpen, setIsAccountModalOpen]: [boolean, Function] = useState(false);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [accountPhotoFiles, setAccountPhotoFiles]: [Record<string, File>, Function] = useState({});
  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [accountActiveId, setAccountActiveId]: [number | null, Function] = useState(null);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);
  const [isSendNotificationModalOpen, setIsSendNotificationModalOpen]: [boolean, Function] = useState(false);
  const [sendNotificationModalErrorMessage, setSendNotificationModalErrorMessage]: [boolean, Function]
    = useState(false);

  const pageTitle: string = 'Settings: Account Administration';
  const createButtonTitle: string = 'New Account';
  const deleteConfirmModalTitle: string = 'Delete Account';
  const deleteConfirmModalText: string = 'Are you sure you want to delete this account?';
  const isFormDataPristine: boolean = isFormPristine && !Object.keys(accountPhotoFiles).length;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;

  const accountListViewPermissions: string[] = useMemo((): string[] => [ACCOUNT_LIST_VIEW], []);
  const saveFormDeps: DepsItem[] = useMemo((): DepsItem[] => [formValues], [formValues]);

  const onCancelForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);

    setAccountActiveId(null);
    setAccountPhotoFiles({});
  }, []);

  const openModal: Function = useCallback((): void => {
    setIsAccountModalOpen(true);
  }, []);

  const closeModal: Function = useCallback((): void => {
    setIsAccountModalOpen(false);
  }, []);

  const onCreateButtonClick: Function = useCallback((): void => {
    openModal();
  }, []);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!accountActiveId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteAccount({
        accountId: accountActiveId,
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [accountActiveId]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const formData: FormValueItem = {
      [formKeyNew]: {
        ...values.accountListData[formKeyNew],
      },
    };

    const accountList: AccountInput[] = [];
    Object.keys(formData).forEach((accountIndex: string): void => {
      const accountFormItem: FormValueItem = {
        ...formData[accountIndex],
      };

      delete accountFormItem.systemAdminUserPasswordConfirm;

      if (accountPhotoFiles[FormService.FORM_KEY_NEW]) {
        accountFormItem.photo = accountPhotoFiles[FormService.FORM_KEY_NEW];
      } else {
        delete accountFormItem.photo;
      }

      const accountItem: AccountInput = accountFormItem;

      accountList.push(accountItem);
    });

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveAccountListData({
        accountListForm: {
          accountList,
        },
      }, (response: ActionResponse): void => {
        closeModal();

        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [isSaveDisabled, accountPhotoFiles, formKeyNew]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [isSaveDisabled, accountPhotoFiles, withFormErrors, formValues]);

  const accountPhotoChange: Function = useCallback((file: File): void => {
    setAccountPhotoFiles({
      ...(accountPhotoFiles || {}),
      [FormService.FORM_KEY_NEW]: file,
    });
  }, [accountPhotoFiles]);

  const accountPhotoDelete: Function = useCallback((): void => {
    setAccountPhotoFiles({});
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const openDeleteConfirmModal: Function = useCallback((accountId: number): void => {
    setAccountActiveId(accountId);
    setIsDeleteConfirmModalOpen(true);
  }, []);

  const closeDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
    setAccountActiveId(null);
  }, []);

  const openSendNotificationModal: Function = useCallback((): void => {
    setIsSendNotificationModalOpen(true);
  }, []);

  const closeSendNotificationModal: Function = useCallback((): void => {
    setIsSendNotificationModalOpen(false);
    setSendNotificationModalErrorMessage(null);
  }, []);

  const onSendNotificationModalFormChange: Function = useCallback((): void => {
    if (sendNotificationModalErrorMessage) {
      setSendNotificationModalErrorMessage(null);
    }
  }, [sendNotificationModalErrorMessage]);

  const onSendNotificationClick: Function = useCallback((
    accountId: number | null, text: string | null, subject: string | null, isImportant: boolean, onSuccess?: Function,
  ): Promise<ActionCallback> | null => {
    if (!text) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(sendAccountNotification({
        notificationForm: {
          accountId: accountId || null,
          text: text || null,
          subject: subject || null,
          isImportant: isImportant || false,
        },
      }, (response: ActionResponse): void => {
        closeSendNotificationModal();

        onCancelForm();

        setStatusMessage('Sent successfully');
        setIsOpenMessage(true);

        if (onSuccess) {
          onSuccess();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setSendNotificationModalErrorMessage(exception.message);
        }
      }));
    });
  }, []);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getSettingsData());
    dispatch(getAccountListData());
  }, [queryTab]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: ACCOUNT_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, accountId: number): void => {
        if (accountId) {
          openDeleteConfirmModal(accountId);
        }
      },
    },
  ], []);

  const controlsActionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <SendNotificationIcon />,
      text: 'Send Notification',
      onClick: (): void => {
        openSendNotificationModal();
      },
      permissions: accountListViewPermissions,
    },
  ], [accountListViewPermissions]);

  const tableAccountListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'photo',
      title: 'Photo',
      component: (account: Account): ReactElement => (
        <TableCell>
          <FilteredLink isExternal link={account.photo || account.photoThumbnail}>
            <Image
              isCentered
              text={account.name}
              link={account.photoThumbnail || account.photo || CompanyDefaultImage}
            />
          </FilteredLink>
        </TableCell>
      ),
    },
    {
      id: 2,
      name: 'name',
      title: 'Name',
      component: (account: Account): ReactElement => (
        <TableCellExtended isText>
          {account.name}
        </TableCellExtended>
      ),
    },
    {
      id: 3,
      name: 'shortName',
      title: 'Short Name',
      component: (account: Account): ReactElement => (
        <TableCell isText>
          {account.shortName}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'role.title',
      title: 'Role',
      component: (account: Account): ReactElement => (
        <TableCell isText>
          {account.role && account.role.title}
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'accountSubscriptionPlan.title',
      title: 'Subscription Plan',
      component: (account: Account): ReactElement => (
        <TableCell isText>
          {account.accountSubscriptionPlan && account.accountSubscriptionPlan.title}
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'systemAdminUser.email',
      title: 'System Admin Email',
      component: (account: Account): ReactElement => (
        <TableCell isText>
          {
            account.systemAdminUser && account.systemAdminUser.email && (
              <FilteredLink isEmail link={account.systemAdminUser.email}>
                {account.systemAdminUser.email}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'actions',
      title: 'Actions',
      isHeaderNameHidden: true,
      component: (account: Account): ReactElement => (
        <TableCell isActions isTextRight>
          {
            !account.isUsed && (
              <CheckAccess
                permissions={actionMenuItems
                  && actionMenuItems.map((actionMenuItem: ActionMenuItem): string | undefined => (
                    actionMenuItem.permission
                  ))}
              >
                <ActionButton
                  withPopup
                  withNoPadding
                  withFixedSize
                  onClick={openActionMenu}
                  {...{
                    [DATA_ACCOUNT_ID]: account.id,
                  }}
                >
                  <MoreIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
        </TableCell>
      ),
    },
  ], []);

  useEffect((): void => {
    dispatch(getSettingsData());
    dispatch(getAccountListData());
  }, []);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  if (!currentUser && !currentUser.isPlatformAdmin) {
    return null;
  }

  return (
    <CheckAccess permissions={accountListViewPermissions}>
      <Form id={FORM_ID_SETTINGS_ACCOUNT_LIST} onChange={onFormChange}>
        <PullToRefresh onRefresh={onRefresh}>
          <ContentHeader title={pageTitle} />
          <SettingsPageTabs
            isCompressed={isRightDrawerOpen}
            activeTab={TAB_NAME_ACCOUNT_ADMINISTRATION}
            currentUser={currentUser}
          />
          {
            !isDataLoaded && (
              <Loader isPageLoader withTopOffset />
            )
          }
          {
            isDataLoaded && (
              <Fragment>
                <Controls
                  withNoEditMode
                  createButtonTitle={createButtonTitle}
                  createPermission={ACCOUNT_CREATE}
                  customActionMenuItems={controlsActionMenuItems}
                  onCreateButtonClick={onCreateButtonClick}
                />
                {
                  accountListData && !!accountListData.length ? (
                    <Card>
                      {
                        !isSmallScreen ? (
                          <ContentListContainerOffset withTabs>
                            <Table withStickyHeader>
                              <TableHead isHeader withFixedCellsSize>
                                <TableRow>
                                  {
                                    tableAccountListFields.map((tableField: TableField): ReactElement => (
                                      <TableCell
                                        isActions={tableField.name === HEADER_NAME_ACTIONS}
                                        isTextRight={tableField.name === HEADER_NAME_ACTIONS}
                                        key={tableField.id}
                                      >
                                        {
                                          !tableField.isHeaderNameHidden ? (
                                            <TextBlock>
                                              {tableField.title}
                                            </TextBlock>
                                          ) : ''
                                        }
                                      </TableCell>
                                    ))
                                  }
                                </TableRow>
                              </TableHead>
                              <TableBody isPlain withZebraRows withFixedCellsSize withBorderedCells>
                                {
                                  accountListData.map((account: Account): ReactElement => (
                                    <TableRow key={account.id}>
                                      {
                                        tableAccountListFields.map((tableField: TableField): ReactElement | boolean => (
                                          !!tableField.component && (
                                            <Fragment key={tableField.id}>
                                              {tableField.component(account)}
                                            </Fragment>
                                          )
                                        ))
                                      }
                                    </TableRow>
                                  ))
                                }
                              </TableBody>
                            </Table>
                          </ContentListContainerOffset>
                        ) : (
                          <ContentListContainerOffset isDefault withTabs>
                            <List>
                              {
                                accountListData.map((account: Account): ReactElement => (
                                  <ListItem key={account.id}>
                                    <FilteredLink isExternal link={account.photo || account.photoThumbnail}>
                                      <ListItemImage
                                        text={account.name}
                                        link={account.photoThumbnail || account.photo || CompanyDefaultImage}
                                      />
                                    </FilteredLink>
                                    <ListItemText isBlock>
                                      {account.name}
                                    </ListItemText>
                                  </ListItem>
                                ))
                              }
                            </List>
                          </ContentListContainerOffset>
                        )
                      }
                    </Card>
                  ) : (
                    <NoContentBlock>
                      No accounts found by your request
                    </NoContentBlock>
                  )
                }
              </Fragment>
            )
          }
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <SettingsAccountModal
          isOpen={isAccountModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          accountPhotoFile={accountPhotoFiles[FormService.FORM_KEY_NEW]}
          accountPhotoChange={accountPhotoChange}
          accountPhotoDelete={accountPhotoDelete}
          roleList={roleList}
          accountSubscriptionPlanList={accountSubscriptionPlanList}
          saveFormDeps={saveFormDeps}
          onCloseModal={closeModal}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeleteConfirm}
          onCloseModal={closeDeleteConfirmModal}
        />
        <ContextMenu
          menuAnchor={menuAnchor}
          actionMenuItems={actionMenuItems}
          dataAttributeEntityId={DATA_ACCOUNT_ID}
          onClose={closeActionMenu}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
      <SettingsAccountNotificationModal
        isOpen={isSendNotificationModalOpen}
        errorMessage={sendNotificationModalErrorMessage}
        accountPlainList={accountPlainList}
        onChange={onSendNotificationModalFormChange}
        onSubmitForm={onSendNotificationClick}
        onCloseModal={closeSendNotificationModal}
      />
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    rightDrawer,
    settings,
    settingsAccountList,
  } = reducers;

  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);

  initialValues.accountListData = {
    [formKeyNew]: {
      name: null,
      role: null,
      accountSubscriptionPlan: ACCOUNT_SUBSCRIPTION_PLAN_DEFAULT,
      systemAdminUserEmail: null,
      systemAdminUserPassword: null,
      systemAdminUserPasswordConfirm: null,
    },
  };

  return {
    isDataLoaded: !!settings.settingsData && !!settingsAccountList.accountListData,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    currentUser: app.baseData && app.baseData.currentUser,
    accountListData: settingsAccountList.accountListData && settingsAccountList.accountListData.accountList,
    roleList: settingsAccountList.accountListData && settingsAccountList.accountListData.roleList,
    accountPlainList: settingsAccountList.accountListData && settingsAccountList.accountListData.accountPlainList,
    accountSubscriptionPlanList: settingsAccountList.accountListData
      && settingsAccountList.accountListData.accountSubscriptionPlanList,
  };
};

const SettingsAccountListForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ settingsAccountList }: Reducers): ValidationErrorsMap => (
    settingsAccountList.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ settingsAccountList }: Reducers): ValidationErrorsMap => (
    settingsAccountList.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_SETTINGS_ACCOUNT_LIST}
      component={SettingsAccountList}
      validator={SettingsAccountListValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(SettingsAccountListForm);
